<template>
  <section id="comments" class="article__comments">
    <header class="mb-6 flex items-center">
      <IconMessage :width="isMobile ? '24px' : '32px'" :height="isMobile ? '24px' : '32px'" />
      <span class="font-semibold text-md sm:text-20 mx-2">{{ $t('article-comment.header') }}</span>
      <div class="border-t border-[#E2E5ED] flex-grow"></div>
    </header>
    <div class="mb-5">
      <CommentBox mode="create" :article-id="article._id" @submit="handleNewComment"></CommentBox>
    </div>
    <OLoading v-if="comments.length === 0 && commentsLoading" class="w-10 mx-auto h-96" />
    <div v-else-if="comments.length > 0" class="tab--comment mb-5 relative text-md border-b border-divider-200">
      <OButton
        pure
        class="btn--tab font-bold px-6 py-4"
        :class="{ 'border-b-4 border-b-o-blue border border-divider-200 rounded-t-md': currentSort === 'recent' }"
        @click="handleChangeTab('recent')"
      >
        {{ $t('article-comment.for-date') }}
      </OButton>
      <OButton
        pure
        class="btn--tab font-bold px-6 py-4"
        :class="{ 'border-b-4 border-b-o-blue border border-divider-200 rounded-t-md': currentSort === 'popular' }"
        @click="handleChangeTab('popular')"
      >
        {{ $t('article-comment.for-smart') }}
      </OButton>
      <div class="border-b border-divider-200 absolute w-full h-px bottom-0 left-0 mb-px -z-1"></div>
    </div>
    <div class="space-y-6 sm:px-2">
      <div
        v-if="currentSort === 'recent' && isInappropriated"
        class="flex gap-2 items-center select-none border-b border-b-gray-200 pb-4 text-primary-200"
      >
        <IconEyeOpen v-if="isShowInappropriate" />
        <IconEyeClose v-else />
        <p v-if="isShowInappropriate" class="text-sm leading-22">
          {{ $t('article-comment.show-offensive-comments') }}
          <strong
            class="cursor-pointer hover:underline"
            @click="setInappropriate()"
            v-html="$t('article-comment.show-offensive-comments-1')"
          ></strong>
        </p>
        <p v-else class="text-sm leading-22">
          {{ $t('article-comment.hide-offensive-comments') }}
          <strong
            class="cursor-pointer hover:underline"
            @click="setInappropriate()"
            v-html="$t('article-comment.hide-offensive-comments-1')"
          ></strong>
          <br />
          {{ $t('article-comment.hide-offensive-comments-2') }}
          <span
            class="cursor-pointer text-o-blue hover:underline"
            @click="openFeedbackModal"
            v-html="$t('article-comment.hide-offensive-comments-3')"
          ></span>
        </p>
      </div>
      <article v-for="(comment, index) in comments" :key="comment._id">
        <CommentItem
          v-if="!comment.isInappropriate || isShowInappropriate"
          :item="comment"
          @reply="handleReply(comment, index)"
          @delete="handleDeleteComment"
        >
          <div v-if="hasReplies(comment)" class="ml-8 mt-4 space-y-6">
            <article v-for="reply in comment.replies.data" :key="reply._id">
              <CommentItem
                v-if="!reply.isInappropriate || isShowInappropriate || (user && reply.user._id === user._id)"
                :item="reply"
                @reply="handleReply(reply, index)"
                @delete="handleDeleteReply"
              ></CommentItem>
            </article>
            <div v-if="hasLoadMore(comment.replies.pagination)" class="u-center">
              <OLoading v-if="repliesLoading === comment._id" />
              <CommentLoadMore v-else :is-reply="true" @click="handleLoadMoreReply(comment)" />
            </div>
          </div>
        </CommentItem>
        <div ref="commentboxRef"></div>
        <CommentBox
          v-if="selectedCommentId === comment._id"
          class="mt-3"
          :comment="selectedComment"
          mode="reply"
          :article-id="article._id"
          @submit="handleNewReply"
        ></CommentBox>
      </article>
      <OLoading v-if="comments.length !== 0 && commentsLoading" class="w-10 mx-auto" />
      <div v-else-if="comments.length !== 0 && hasLoadMore(commentsPagination)" class="u-center">
        <CommentLoadMore @click="handleLoadMoreComments" />
      </div>
      <div
        v-if="isInappropriated && currentSort === 'popular'"
        class="flex gap-2 items-center select-none border-t border-t-gray-200 pt-4 text-primary-200 leading-22"
      >
        <IconEyeClose />
        <p class="text-sm" v-html="$t('article-comment.offensive-comments-description')"></p>
      </div>
    </div>
  </section>
</template>
<script>
import { defineComponent, inject, nextTick, onMounted, ref, useContext, provide, useFetch, computed } from '@nuxtjs/composition-api';
import { uniqBy } from 'lodash';
import CommentItem from './CommentItem';
import CommentBox from './CommentBox.vue';
import CommentLoadMore from './CommentLoadMore.vue';
import { OLoading, OButton } from '~/modules/common/components/ui';
import { useDevice } from '~/composables/useDevice';
import { useDialog, useModal } from '~/composables/useModal';
import { useAuth } from '~/modules/auth/state/useAuth';

import IconMessage from '~/static/images/messages.svg?inline';
import IconEyeOpen from '~/static/images/icon/eyeOpen.svg?inline';
import IconEyeClose from '~/static/images/icon/eyeClose.svg?inline';

export default defineComponent({
  components: { OButton, OLoading, CommentItem, CommentBox, CommentLoadMore, IconMessage, IconEyeClose, IconEyeOpen },
  setup() {
    const { $service } = useContext();
    const { isMobile, isBot } = useDevice();
    const { openErrorDialog } = useDialog();
    const { isLoggedIn, user } = useAuth();

    const commentboxRef = ref();

    const article = inject('article');
    const adConfig = inject('adConfig');
    const comments = ref([]);
    const commentsLoading = ref(true);
    const repliesLoading = ref(null);
    const commentsPagination = ref({});
    provide('comments', {
      comments,
    });
    const currentSort = ref('popular');
    const initialLimit = 3;
    const commentLimit = ref(initialLimit);
    const isInappropriated = ref(false);
    const isShowInappropriate = ref(false);

    const selectedComment = ref();
    const selectedCommentId = ref();
    const { openModal } = useModal();

    const openFeedbackModal = () => {
      openModal(import('~/modules/common/components/modals/ModalFeedback' /* webpackChunkName: "ModalFeedback" */));
    };

    const resetSelectedComment = () => {
      selectedComment.value = null;
      selectedCommentId.value = null;
    };

    const checkSponsoredArticle = computed(() => {
      return adConfig.value.decidedMode.value === 'sponsored';
    });

    if (isBot.value) {
      useFetch(async () => {
        await getComments(article._id, currentSort.value, 1);
      });
    }

    const checkOffensiveComments = (comments) => {
      return comments.some((c) => {
        const isMe = isLoggedIn.value && c.user?._id === user.value._id;
        if (isMe) c.isInappropriate = false;
        return c.isInappropriate;
      });
    };

    const setInappropriate = () => {
      isShowInappropriate.value = !isShowInappropriate.value;
    };

    const getComments = async (id, sort, page) => {
      commentsLoading.value = true;
      const prevSort = currentSort.value;
      currentSort.value = sort;
      const limit = commentLimit.value;
      try {
        const { data, pagination } = await $service.article.getComments({ id, sort, page, limit });
        const firstPageList = comments.value.length === 0 ? data : comments.value.concat(data.slice(initialLimit));
        isInappropriated.value = checkOffensiveComments(data);
        comments.value = page === 1 ? firstPageList : comments.value.concat(data);
        commentsPagination.value = pagination;
      } catch (error) {
        currentSort.value = prevSort;
        openErrorDialog({ error });
      } finally {
        commentsLoading.value = false;
      }
    };

    const handleChangeTab = async (sort) => {
      if (sort === 'popular') isShowInappropriate.value = false;
      comments.value = [];
      commentLimit.value = initialLimit;
      await getComments(article._id, sort);
    };

    const handleReply = (comment, index) => {
      selectedCommentId.value = null;
      selectedComment.value = comment;
      if (comment.parent?._id) {
        selectedCommentId.value = comment.parent._id; // parent var ise child
      } else {
        selectedCommentId.value = comment._id; // parent
      }
      scrollToCommentBox(index);
    };

    const handleNewComment = (comment) => {
      comments.value.unshift(comment);
    };

    const handleNewReply = (comment) => {
      const commentsIndex = comments.value.findIndex((item) => item._id === selectedCommentId.value);
      const list = comments.value[commentsIndex];
      if (!list.replies?.data?.length) {
        list.replies = {
          pagination: {},
          data: [],
        };
      }
      list.replies = { pagination: list.replies.pagination, data: list.replies.data.concat(comment) };
      resetSelectedComment();
    };

    const handleReplyThread = (comment) => {
      comments.value.unshift(comment);
      resetSelectedComment();
    };

    const handleLoadMoreReply = async (comment) => {
      repliesLoading.value = comment._id;
      const page = comment.replies.pagination.page;
      const currentLimit = comment.replies.pagination.limit;
      const isFirstPage = page === 1 && currentLimit === 1;
      // ilk açılışta apiden default 1 adet yorum geliyor
      // sonraki isteklerde 15 adet istiyoruz  o yüzden ilk sayfayı yeniden istiyoruz
      const nextPage = isFirstPage ? 1 : comment.replies.pagination.page + 1;
      const { data, pagination } = await $service.article.getReplies({ page: nextPage, id: comment._id });
      comment.replies.data = isFirstPage ? data : comment.replies.data.concat(data);
      // yeni eklenmis yorum icin uniq olma kontrolu
      comment.replies.data = uniqBy(comment.replies.data.reverse(), (item) => item._id).reverse();
      comment.replies.pagination = pagination;
      repliesLoading.value = null;
    };

    const handleLoadMoreComments = async () => {
      // ilk açılışta 3 adet yorum getiriyoruz
      // sonraki isteklerde limit 15 olarak değişiyor o yüzden ilk sayfayı yeniden istiyoruz
      const nextPage = commentLimit.value === initialLimit ? 1 : commentsPagination.value.page + 1;
      commentLimit.value = 15;
      await getComments(article._id, currentSort.value, nextPage);
    };

    const hasReplies = (comment) => {
      return comment.replies?.data?.length > 0;
    };

    /**
     * Yoruma verilen yanıtlar 5'den fazlaysa "daha fazla göster" butonu gözükür.
     */
    const hasLoadMore = (pagination) => {
      return pagination.hasNextPage;
    };

    const handleDeleteComment = (comment) => {
      comments.value = comments.value.filter((item) => item._id !== comment._id);
    };

    const handleDeleteReply = (comment) => {
      const parentId = comment.parent._id;
      const commentIndex = comments.value.findIndex((item) => item._id === parentId);
      const commentReplies = comments.value[commentIndex];
      commentReplies.replies.data = commentReplies.replies.data.filter((reply) => reply._id !== comment._id);
    };

    function scrollToCommentBox(index) {
      nextTick(() => {
        commentboxRef.value[index].scrollIntoView({ block: 'center' });
      });
    }

    onMounted(async () => {
      const page = 1;
      if (!isBot.value) await getComments(article._id, currentSort.value, page);
    });

    return {
      article,
      isMobile,
      comments,
      commentsLoading,
      repliesLoading,
      commentsPagination,
      handleChangeTab,
      handleReply,
      handleReplyThread,
      currentSort,
      selectedComment,
      selectedCommentId,
      handleNewComment,
      handleNewReply,
      handleLoadMoreReply,
      handleLoadMoreComments,
      handleDeleteComment,
      handleDeleteReply,
      hasReplies,
      hasLoadMore,
      commentboxRef,
      openFeedbackModal,
      checkSponsoredArticle,
      isInappropriated,
      isShowInappropriate,
      setInappropriate,
      user,
    };
  },
});
</script>
